import { isValid, format } from 'date-fns'
import { formatInTimeZone, fromZonedTime, toZonedTime } from 'date-fns-tz'
import { getTenantTimezoneCode } from './tenant'
import { getTenantTimeZone } from './tenant-timezone'

const ISO_DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/

const TIME_FORMAT_PATTERN = /[hHaKk]/

export const withTenantTimezoneSuffix = text => {
  const timezoneCode = getTenantTimezoneCode()
  if (!text || !timezoneCode) return text || ''
  return `${text} (${timezoneCode})`
}

/** Formatted time/datetime for display, with tenant timezone name when the format includes time. */
export const formatDisplayTime = (date, dateFormat = 'hh:mm a') => {
  const formatted = formatUtcDate(date, dateFormat)
  if (!formatted) return ''
  return TIME_FORMAT_PATTERN.test(dateFormat)
    ? withTenantTimezoneSuffix(formatted)
    : formatted
}

const isIsoCalendarDate = value =>
  typeof value === 'string' && ISO_DATE_REGEX.test(value)

/** Format yyyy-MM-dd as a calendar day in the tenant timezone. */
export const formatTenantCalendarDate = (
  isoDate,
  dateFormat = 'MM/dd/yyyy'
) => {
  if (!isoDate) return ''
  if (!isIsoCalendarDate(isoDate)) return ''

  const [year, month, day] = isoDate.split('-').map(Number)
  const timeZone = getTenantTimeZone()
  const localMidnight = new Date(year, month - 1, day, 0, 0, 0, 0)

  if (timeZone) {
    const instant = fromZonedTime(localMidnight, timeZone)
    return formatInTimeZone(instant, timeZone, dateFormat)
  }

  return format(localMidnight, dateFormat)
}

export const formatDate = date => {
  if (!date) return ''

  const dateObj = new Date(date)
  return dateObj.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}

export const formatToLocaleDate = date => {
  if (!date) return ''
  return format(date, 'MM/dd/yyyy')
}

export const isValidDate = date => {
  if (!date) return false
  return isValid(date)
}

// Format a single time from a date (e.g., "9am" or "5:30pm")
export const formatTime = date => {
  if (!date) return ''

  const dateObj = new Date(date)
  if (!isValid(dateObj)) return ''

  const hours = dateObj.getHours()
  const minutes = dateObj.getMinutes()

  // Convert to 12-hour format
  const period = hours >= 12 ? 'pm' : 'am'
  const hour12 = hours % 12 || 12

  // Only include minutes if they're not zero
  if (minutes === 0) {
    return `${hour12}${period}`
  }

  return `${hour12}:${minutes.toString().padStart(2, '0')}${period}`
}

export const timeToMinutes = timeStr => {
  if (!timeStr) return 0

  const match = String(timeStr).match(/^(\d{1,2})(?::(\d{2}))?\s*(AM|PM)/i)
  if (!match) return 0

  let hours = Number(match[1])
  const minutes = Number(match[2] || 0)
  const period = match[3].toUpperCase()

  if (period === 'PM' && hours !== 12) hours += 12
  if (period === 'AM' && hours === 12) hours = 0

  return hours * 60 + minutes
}

export const formatDateString = (dateString, dateFormat = 'MM/dd/yyyy') => {
  if (!dateString) return ''

  if (isIsoCalendarDate(dateString)) {
    return formatTenantCalendarDate(dateString, dateFormat)
  }

  return formatUtcDate(dateString, dateFormat)
}

export const formatUtcDate = (date, dateFormat = 'MM/dd/yyyy') => {
  if (!date) return ''

  if (isIsoCalendarDate(date)) {
    return formatTenantCalendarDate(date, dateFormat)
  }

  const timeZone = getTenantTimeZone()
  if (!timeZone) return ''

  const zonedDate = toZonedTime(new Date(date), timeZone)
  return format(zonedDate, dateFormat, { timeZone })
}

export const formatUtcTimeRange = (startDate, endDate) => {
  if (!startDate || !endDate) return ''

  const startTime = formatUtcDate(startDate, 'hh:mm a')
  const endTime = formatUtcDate(endDate, 'hh:mm a')

  if (!startTime || !endTime) return ''

  return withTenantTimezoneSuffix(`${startTime} - ${endTime}`)
}

export const addDaysToISODate = (isoDate, days) => {
  const [year, month, day] = isoDate.split('-').map(Number)
  const date = new Date(year, month - 1, day)
  date.setDate(date.getDate() + days)
  const newYear = date.getFullYear()
  const newMonth = String(date.getMonth() + 1).padStart(2, '0')
  const newDay = String(date.getDate()).padStart(2, '0')
  return `${newYear}-${newMonth}-${newDay}`
}

export const isoDateToDisplayDate = isoDate => {
  if (!isoDate) return ''
  return formatTenantCalendarDate(isoDate, 'MM-dd-yyyy')
}

/** e.g. "05/25/2026, Monday" — date and weekday in tenant timezone */
export const formatIsoDateWithWeekday = (
  isoDate,
  { weekdayFormat = 'EEEE', dateFormat = 'MM/dd/yyyy', separator = ', ' } = {}
) => {
  if (!isoDate) return ''

  if (isIsoCalendarDate(isoDate)) {
    const weekday = formatTenantCalendarDate(isoDate, weekdayFormat)
    const formattedDate = formatTenantCalendarDate(isoDate, dateFormat)
    return `${formattedDate}${separator}${weekday}`
  }

  const weekday = formatUtcDate(isoDate, weekdayFormat)
  const formattedDate = formatUtcDate(isoDate, dateFormat)
  return `${formattedDate}${separator}${weekday}`
}

export const isoDateToWeekday = (isoDate, weekdayFormat = 'EEE') => {
  if (!isoDate) return ''

  if (isIsoCalendarDate(isoDate)) {
    return formatTenantCalendarDate(isoDate, weekdayFormat)
  }

  return formatUtcDate(isoDate, weekdayFormat)
}

export const toUTCDateString = value => {
  if (typeof value === 'string') {
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/
    if (dateRegex.test(value)) {
      return value
    }
  }

  let date

  if (value instanceof Date) date = value
  else date = new Date(value)

  if (isNaN(date.getTime())) {
    throw new Error('Invalid date provided to toUTCDateString')
  }

  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')

  return `${year}-${month}-${day}`
}

export const getTodayIso = () => {
  const today = new Date()
  const year = today.getFullYear()
  const month = String(today.getMonth() + 1).padStart(2, '0')
  const day = String(today.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

/** Calendar-day yyyy-MM-dd in tenant timezone (falls back to browser local). */
export const getTenantTodayIso = () => {
  const timeZone = getTenantTimeZone()
  if (!timeZone) return getTodayIso()
  return formatInTimeZone(new Date(), timeZone, 'yyyy-MM-dd')
}
